import type { Settings } from '../settings/schema.ts';
import { DEFAULT_SETTINGS } from '../settings/schema.ts';

export type ShadowPreset = '' | 'raised' | 'depressed' | 'outline' | 'shadow';
export type BoxShadowPreset = '' | 'soft' | 'medium';

export type TrackStyle = {
  fontSizeRatio: number;
  fontWeight: number;
  fontColor: string;
  strokeWidth: number;
  strokeColor: string;
  shadow: ShadowPreset;
  shadowColor: string;
  fontBackgroundColor: string;
  fontBackgroundColorOpacity: number;
  fontFamily: string;
  fontGoogleFamilies: string;
  fontGoogleCssProperties: string;
  /** letter-spacing in em */
  letterSpacing: number;
  /** unitless line-height */
  lineHeight: number;
  /** uniform padding in px */
  padding: number;
  /** element opacity 0-1 */
  opacity: number;
  boxShadow: BoxShadowPreset;
};

export type StylePresetId =
  | 'classic'
  | 'clean-white'
  | 'soft-box'
  | 'outline-bold'
  | 'raised-glow'
  | 'karaoke-yellow';

export type StylePresetPreview = {
  /** Primary preview line (typically source language). */
  main: string;
  /** Secondary preview line (typically translation). */
  second: string;
  /** Short sample for preset card thumbnails. */
  thumb: string;
};

export type StylePreset = {
  id: StylePresetId;
  /** Extension i18n message key */
  nameKey: string;
  style: TrackStyle;
  /** Optional language-specific preview copy. */
  preview?: StylePresetPreview;
};

export type StyleApplyScope = 'main' | 'second' | 'both';

const NETFLIX_STACK =
  'Netflix Sans,Helvetica Neue,Segoe UI,Roboto,Ubuntu,sans-serif';

const TYPO_DEFAULTS: Pick<
  TrackStyle,
  'letterSpacing' | 'lineHeight' | 'padding' | 'opacity' | 'boxShadow'
> = {
  letterSpacing: 0,
  lineHeight: 1.35,
  padding: 8,
  opacity: 1,
  boxShadow: '',
};

export const STYLE_PRESETS: StylePreset[] = [
  {
    id: 'classic',
    nameKey: 'style_studio_preset_classic',
    preview: {
      main: 'Hello, how are you?',
      second: '你好，你好嗎？',
      thumb: 'Aa',
    },
    style: {
      ...TYPO_DEFAULTS,
      fontSizeRatio: 0.9,
      fontWeight: 500,
      fontColor: '#FFFFFF',
      strokeWidth: 0,
      strokeColor: '#000000',
      shadow: '',
      shadowColor: '#000000',
      fontBackgroundColor: '#000000',
      fontBackgroundColorOpacity: 0.55,
      fontFamily: NETFLIX_STACK,
      fontGoogleFamilies: '',
      fontGoogleCssProperties: '',
    },
  },
  {
    // White fill + thick black edge, no box.
    id: 'clean-white',
    nameKey: 'style_studio_preset_clean_white',
    preview: {
      main: 'また会えるといいね。',
      second: 'Hope we meet again.',
      thumb: '字幕',
    },
    style: {
      ...TYPO_DEFAULTS,
      fontSizeRatio: 1.05,
      fontWeight: 700,
      fontColor: '#FFFFFF',
      strokeWidth: 3.5,
      strokeColor: '#000000',
      shadow: 'outline',
      shadowColor: '#000000',
      fontBackgroundColor: '#000000',
      fontBackgroundColorOpacity: 0,
      fontFamily: NETFLIX_STACK,
      fontGoogleFamilies: '',
      fontGoogleCssProperties: '',
    },
  },
  {
    id: 'soft-box',
    nameKey: 'style_studio_preset_soft_box',
    preview: {
      main: 'Hello, how are you?',
      second: '你好，你好嗎？',
      thumb: 'Aa',
    },
    style: {
      ...TYPO_DEFAULTS,
      padding: 10,
      boxShadow: 'soft',
      fontSizeRatio: 0.9,
      fontWeight: 500,
      fontColor: '#FFFFFF',
      strokeWidth: 0,
      strokeColor: '#000000',
      shadow: '',
      shadowColor: '#000000',
      fontBackgroundColor: '#000000',
      fontBackgroundColorOpacity: 0.65,
      fontFamily: NETFLIX_STACK,
      fontGoogleFamilies: '',
      fontGoogleCssProperties: '',
    },
  },
  {
    id: 'outline-bold',
    nameKey: 'style_studio_preset_outline_bold',
    preview: {
      main: 'Hello, how are you?',
      second: '你好，你好嗎？',
      thumb: 'Aa',
    },
    style: {
      ...TYPO_DEFAULTS,
      fontSizeRatio: 1.0,
      fontWeight: 700,
      fontColor: '#FFFFFF',
      strokeWidth: 2.5,
      strokeColor: '#000000',
      shadow: 'outline',
      shadowColor: '#000000',
      fontBackgroundColor: '#000000',
      fontBackgroundColorOpacity: 0,
      fontFamily: NETFLIX_STACK,
      fontGoogleFamilies: '',
      fontGoogleCssProperties: '',
    },
  },
  {
    id: 'raised-glow',
    nameKey: 'style_studio_preset_raised_glow',
    preview: {
      main: 'Hello, how are you?',
      second: '你好，你好嗎？',
      thumb: 'Aa',
    },
    style: {
      ...TYPO_DEFAULTS,
      fontSizeRatio: 0.95,
      fontWeight: 600,
      fontColor: '#FFFFFF',
      strokeWidth: 0.5,
      strokeColor: '#000000',
      shadow: 'shadow',
      shadowColor: '#000000',
      fontBackgroundColor: '#000000',
      fontBackgroundColorOpacity: 0,
      fontFamily: NETFLIX_STACK,
      fontGoogleFamilies: '',
      fontGoogleCssProperties: '',
    },
  },
  {
    id: 'karaoke-yellow',
    nameKey: 'style_studio_preset_karaoke_yellow',
    preview: {
      main: 'Hello, how are you?',
      second: '你好，你好嗎？',
      thumb: '♪Aa',
    },
    style: {
      ...TYPO_DEFAULTS,
      fontSizeRatio: 1.05,
      fontWeight: 700,
      fontColor: '#FFE066',
      strokeWidth: 2.5,
      strokeColor: '#000000',
      shadow: 'outline',
      shadowColor: '#000000',
      fontBackgroundColor: '#000000',
      fontBackgroundColorOpacity: 0,
      fontFamily: NETFLIX_STACK,
      fontGoogleFamilies: '',
      fontGoogleCssProperties: '',
    },
  },
];

export function getStylePreset(id: string): StylePreset | undefined {
  return STYLE_PRESETS.find((preset) => preset.id === id);
}

function trackKeys(scope: 'main' | 'second', style: TrackStyle): Partial<Settings> {
  const p = scope === 'main' ? 'main' : 'second';
  return {
    [`${p}FontSizeRatio`]: style.fontSizeRatio,
    [`${p}FontWeight`]: style.fontWeight,
    [`${p}FontColor`]: style.fontColor,
    [`${p}StrokeWidth`]: style.strokeWidth,
    [`${p}StrokeColor`]: style.strokeColor,
    [`${p}Shadow`]: style.shadow,
    [`${p}ShadowColor`]: style.shadowColor,
    [`${p}FontBackgroundColor`]: style.fontBackgroundColor,
    [`${p}FontBackgroundColorOpacity`]: style.fontBackgroundColorOpacity,
    [`${p}FontFamily`]: style.fontFamily,
    [`${p}FontGoogleFamilies`]: style.fontGoogleFamilies,
    [`${p}FontGoogleCssProperties`]: style.fontGoogleCssProperties,
    [`${p}LetterSpacing`]: style.letterSpacing,
    [`${p}LineHeight`]: style.lineHeight,
    [`${p}Padding`]: style.padding,
    [`${p}Opacity`]: style.opacity,
    [`${p}BoxShadow`]: style.boxShadow,
  } as Partial<Settings>;
}

/** Expand a track style into extension sync storage style keys for the given scope. */
export function applyTrackStyleToSettings(
  style: TrackStyle,
  scope: StyleApplyScope,
): Partial<Settings> {
  if (scope === 'main') return trackKeys('main', style);
  if (scope === 'second') return trackKeys('second', style);
  return { ...trackKeys('main', style), ...trackKeys('second', style) };
}

/** Schema defaults for one subtitle track's style keys only. */
export function getTrackStyleResetDefaults(slot: 'main' | 'second'): Partial<Settings> {
  return applyTrackStyleToSettings(trackStyleFromSettings(DEFAULT_SETTINGS, slot), slot);
}

/** Copy style keys from one track onto the other. */
export function copyTrackStyleBetweenSlots(
  settings: Partial<Settings>,
  from: 'main' | 'second',
  to: 'main' | 'second',
): Partial<Settings> {
  const merged = { ...DEFAULT_SETTINGS, ...settings };
  return applyTrackStyleToSettings(trackStyleFromSettings(merged, from), to);
}

/** Expand a preset into extension sync storage style keys for the given scope. */
export function applyPresetToSettings(
  preset: StylePreset,
  scope: StyleApplyScope,
): Partial<Settings> {
  return applyTrackStyleToSettings(preset.style, scope);
}

export function trackStyleFromSettings(
  settings: Pick<
    Settings,
    | 'mainFontSizeRatio'
    | 'mainFontWeight'
    | 'mainFontColor'
    | 'mainStrokeWidth'
    | 'mainStrokeColor'
    | 'mainShadow'
    | 'mainShadowColor'
    | 'mainFontBackgroundColor'
    | 'mainFontBackgroundColorOpacity'
    | 'mainFontFamily'
    | 'mainFontGoogleFamilies'
    | 'mainFontGoogleCssProperties'
    | 'mainLetterSpacing'
    | 'mainLineHeight'
    | 'mainPadding'
    | 'mainOpacity'
    | 'mainBoxShadow'
    | 'secondFontSizeRatio'
    | 'secondFontWeight'
    | 'secondFontColor'
    | 'secondStrokeWidth'
    | 'secondStrokeColor'
    | 'secondShadow'
    | 'secondShadowColor'
    | 'secondFontBackgroundColor'
    | 'secondFontBackgroundColorOpacity'
    | 'secondFontFamily'
    | 'secondFontGoogleFamilies'
    | 'secondFontGoogleCssProperties'
    | 'secondLetterSpacing'
    | 'secondLineHeight'
    | 'secondPadding'
    | 'secondOpacity'
    | 'secondBoxShadow'
  >,
  slot: 'main' | 'second',
): TrackStyle {
  if (slot === 'main') {
    return {
      fontSizeRatio: settings.mainFontSizeRatio,
      fontWeight: settings.mainFontWeight,
      fontColor: settings.mainFontColor,
      strokeWidth: settings.mainStrokeWidth,
      strokeColor: settings.mainStrokeColor,
      shadow: (settings.mainShadow || '') as ShadowPreset,
      shadowColor: settings.mainShadowColor,
      fontBackgroundColor: settings.mainFontBackgroundColor,
      fontBackgroundColorOpacity: settings.mainFontBackgroundColorOpacity,
      fontFamily: settings.mainFontFamily,
      fontGoogleFamilies: settings.mainFontGoogleFamilies,
      fontGoogleCssProperties: settings.mainFontGoogleCssProperties,
      letterSpacing: settings.mainLetterSpacing,
      lineHeight: settings.mainLineHeight,
      padding: settings.mainPadding,
      opacity: settings.mainOpacity,
      boxShadow: (settings.mainBoxShadow || '') as BoxShadowPreset,
    };
  }
  return {
    fontSizeRatio: settings.secondFontSizeRatio,
    fontWeight: settings.secondFontWeight,
    fontColor: settings.secondFontColor,
    strokeWidth: settings.secondStrokeWidth,
    strokeColor: settings.secondStrokeColor,
    shadow: (settings.secondShadow || '') as ShadowPreset,
    shadowColor: settings.secondShadowColor,
    fontBackgroundColor: settings.secondFontBackgroundColor,
    fontBackgroundColorOpacity: settings.secondFontBackgroundColorOpacity,
    fontFamily: settings.secondFontFamily,
    fontGoogleFamilies: settings.secondFontGoogleFamilies,
    fontGoogleCssProperties: settings.secondFontGoogleCssProperties,
    letterSpacing: settings.secondLetterSpacing,
    lineHeight: settings.secondLineHeight,
    padding: settings.secondPadding,
    opacity: settings.secondOpacity,
    boxShadow: (settings.secondBoxShadow || '') as BoxShadowPreset,
  };
}

